import { useEffect, useMemo, useState, useRef } from "react";
import { ISidebarItem } from "../../../../components/common/sidebar/SidebarItem";
import { useCreativeLibraryStore } from "../../store";
import { useParams } from "react-router-dom";
import { fetchCreativeCollection, updateCreative } from "../../apiService";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ArrowUpDown, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ExploreTabEmptyState } from "../EmptyState";
import { MasonryColumns } from "../MasonryColumn";
import { AdCreative, PublishAdCreative } from "../../types";
import { PollingAdCard } from "@/components/common/adCard";
import DownloadAction from "@/components/common/adCard/actions/DownloadAction";
import BookmarkAction from "../adActions/BookmarkAction";
import PublishAction from "../adActions/PublishAction";
import ReportTitle from "@/screens/analysis/components/report/ReportTitle";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Action } from "@/components/common/adCard/types";
import { useGlobalStore } from "@/GlobalStore";
import PageLoader from "@/components/common/loaders/PageLoader";
import { RiCompass3Line } from "@remixicon/react";

export const ExploreTab: ISidebarItem = {
  type: "tab",
  value: "explore",
  label: "Explore",
  icon: RiCompass3Line,
  id: "explore",
  route: "explore",
};
const TITLE_HEIGHT = 56;
const ExploreDashboard = () => {
  const {
    setExploreAdCreatives,
    setLoadingExploreCreatives,
    exploreAdCreatives,
    loadingExploreCreatives,
    setSelectedSidebarItem,
    selectedSidebarItem,
  } = useCreativeLibraryStore();
  const { selectedAccount, sidebarCollapsed } = useGlobalStore();

  // setting initial tab
  useEffect(() => {
    if (selectedSidebarItem?.id !== ExploreTab.id) setSelectedSidebarItem(ExploreTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const contentAreaRef = useRef<HTMLDivElement>(null);

  const { accountId } = useParams();
  const getCreativeCollection = async (folderId?: string) => {
    if (!accountId) return;
    try {
      setLoadingExploreCreatives(true);
      const res = await fetchCreativeCollection(accountId, folderId);
      setExploreAdCreatives(res.data);
    } catch (error) {
      console.error("Error fetching creative collection:", error);
    } finally {
      setLoadingExploreCreatives(false);
    }
  };

  useEffect(
    () => void getCreativeCollection(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accountId],
  );
  const [sortOrder, setSortOrder] = useState<"newest-first" | "oldest-first">(
    "newest-first",
  );

  // Motion value controlling header translate-Y (negative → hidden)
  const headerOffset = useMotionValue(0);
  // Add a spring to smooth out abrupt changes
  const headerY = useSpring(headerOffset, { stiffness: 400, damping: 40 });
  // Derived height & opacity so layout space collapses
  const headerHeight = useTransform(headerOffset, [-TITLE_HEIGHT, 0], [0, TITLE_HEIGHT]);
  const headerOpacity = useTransform(headerOffset, [-TITLE_HEIGHT, 0], [0, 1]);

  // Attach scroll listener for smooth header + pagination
  useEffect(() => {
    const container = contentAreaRef.current;
    if (!container) return;

    const onScroll = () => {
      // Map scrollTop 0→headerHeight to header slide
      const newOffset = -Math.min(container.scrollTop, TITLE_HEIGHT); // -56 (hidden) → 0 (visible)
      headerOffset.set(newOffset);
    };

    // Initial position (in case user reloads mid-scroll)
    onScroll();

    container.addEventListener("scroll", onScroll);
    return () => {
      container.removeEventListener("scroll", onScroll);
    };
  }, [headerOffset]);

  const orderedAdCreatives = useMemo(() => {
    return [...exploreAdCreatives].sort((a, b) => {
      const dateA = a.created_at ? new Date(a.created_at.split(".")[0]) : new Date(0);
      const dateB = b.created_at ? new Date(b.created_at.split(".")[0]) : new Date(0);

      if (sortOrder === "newest-first") {
        return dateB.getTime() - dateA.getTime();
      }
      return dateA.getTime() - dateB.getTime();
    });
  }, [exploreAdCreatives, sortOrder]);

  const displayText =
    sortOrder === "newest-first" ? "Latest to Oldest" : "Oldest to Latest";

  const handleBookmarkCreative = async (
    adCreative: AdCreative,
    folderId: string | null,
  ) => {
    // optimistic approach
    if (!accountId) return;
    const _oldAdCreatives = [...exploreAdCreatives];
    try {
      setExploreAdCreatives(
        exploreAdCreatives.map((existingCreative) =>
          existingCreative.id === adCreative.id
            ? { ...existingCreative, folder_id: folderId }
            : existingCreative,
        ),
      );
      await updateCreative(accountId, adCreative.id, folderId);
    } catch (error) {
      setExploreAdCreatives(_oldAdCreatives); // reverts changes if api fails
      console.error("Error updating creative:", error);
    }
  };

  const handlePollingComplete = (creative: AdCreative) => {
    const updatedAdCreatives = exploreAdCreatives.map((existingCreative) => {
      if (existingCreative.id === creative.id) {
        return creative; // Replace with the updated creative from polling
      }
      return existingCreative;
    });
    setExploreAdCreatives(updatedAdCreatives);
  };

  const handleOnPublish = (publishAdCreative: PublishAdCreative) => {
    const base = publishAdCreative.ad_creative;
    if (!base) return;
    const updatedAdCreatives = exploreAdCreatives.map((existingAdCreative) =>
      existingAdCreative.id === base.id ? base : existingAdCreative,
    );
    setExploreAdCreatives(updatedAdCreatives);
  };

  return (
    <div className="flex flex-col w-full h-full">
      <div className="flex gap-2 pt-[8px] pb-2 pl-[9px]">
        {sidebarCollapsed && <div className="h-[32px] w-[32px] shrink-0"></div>}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="sq-outline" className="flex items-center gap-2 w-[154px]">
              <ArrowUpDown
                size={14}
                className="text-brand-primary-400"
                strokeWidth={1.8}
              />
              <span className="font-['Helvetica_Neue'] text-[11px] font-normal leading-[19.16px] tracking-[0.02em] text-brand-primary-800">
                {displayText}
              </span>
              <ChevronDown
                size={14}
                className="text-brand-primary-400"
                strokeWidth={1.8}
              />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-[154px] !rounded-[8px]">
            <DropdownMenuItem
              onClick={() => setSortOrder("newest-first")}
              className="flex items-center px-4 py-2 text-[12px] font-normal leading-[19.16px] tracking-[0.02em] text-brand-primary-new hover:bg-[#F7F7F3] rounded-[4px]"
            >
              Latest to Oldest
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => setSortOrder("oldest-first")}
              className="flex items-center px-4 py-2 text-[12px] font-normal leading-[19.16px] tracking-[0.02em] text-brand-primary-new hover:bg-[#F7F7F3] rounded-[4px]"
            >
              Oldest to Latest
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {/* Header — single element animates height + slide + fade */}
      <motion.div
        style={{
          y: headerY,
          opacity: headerOpacity,
          height: headerHeight,
          overflow: "hidden",
        }}
        className="flex items-end gap-2 pl-[9px]"
      >
        <ReportTitle
          title={ExploreTab.label}
          icon={<ExploreTab.icon size={16} className="text-brand-orange" />}
          iconBackgroundColor="#FF45000F"
        />
      </motion.div>
      {/* Content Area */}
      <div
        ref={contentAreaRef}
        className="flex-1 flex flex-col overflow-y-auto px-[9px] py-6"
      >
        {/* creatives */}
        {loadingExploreCreatives ? (
          <div className="flex items-center justify-center h-[80%] w-full">
            <PageLoader />
          </div>
        ) : orderedAdCreatives.length === 0 ? (
          <ExploreTabEmptyState className="-translate-y-[70px] -translate-x-[32px]" />
        ) : (
          <MasonryColumns
            items={orderedAdCreatives}
            cardWidth={324}
            cardGap={14}
            renderItem={(adCreative) => {
              const actions: Action[] = [
                {
                  component: (
                    <DownloadAction
                      url={
                        adCreative.creative_type === "video"
                          ? adCreative.creative?.videos?.[0]?.permalink_url
                          : adCreative.creative?.images?.[0]?.uncompressed_url ||
                            adCreative.creative?.images?.[0]?.permalink_url
                      }
                      type={adCreative.creative_type}
                      aspectRatio={
                        adCreative.creative_type === "video"
                          ? adCreative.creative?.videos?.[0]?.aspect_ratio
                          : adCreative.creative?.images?.[0]?.aspect_ratio
                      }
                    />
                  ),
                  position: "top-right",
                  visibility: "hover",
                },
                {
                  component: (
                    <BookmarkAction
                      folderId={adCreative.folder_id}
                      onBookmark={(folderId) =>
                        void handleBookmarkCreative(adCreative, folderId)
                      }
                    />
                  ),
                  position: "top-right",
                  visibility: "always",
                },
                {
                  component: (
                    <PublishAction
                      published={!!adCreative?.publish_creative?.status_message}
                      creativeId={adCreative?.id}
                      statusMessage={adCreative?.publish_creative?.status_message}
                      onPublish={(publishAdCreative) =>
                        handleOnPublish(publishAdCreative)
                      }
                    />
                  ),
                  position: "bottom-left",
                  visibility: "hover",
                },
              ];
              return (
                <PollingAdCard
                  accountIconUrl={selectedAccount?.additional_info?.ad_account_icon_url}
                  key={adCreative.id}
                  className="min-w-[296px] flex-shrink-0 p-2 rounded-[16px]"
                  adCreative={adCreative}
                  mediaActions={actions}
                  onPollingComplete={handlePollingComplete}
                />
              );
            }}
          />
        )}
      </div>
    </div>
  );
};

export default ExploreDashboard;
